import React, { useRef, useCallback, useEffect, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DatesSetArg, DayCellContentArg } from '@fullcalendar/core';
import type { DateClickArg } from '@fullcalendar/interaction';
import {
  Box,
  IconButton,
  Typography,
  Stack,
  GlobalStyles,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { CalendarDot } from '../../types/calendar';

export type CalendarView = 'month' | 'week';

interface MiniCalendarProps {
  year: number;
  month: number;
  calendarDots: CalendarDot[];
  selectedDate: string | null;
  onDateClick: (date: string) => void;
  onMonthChange: (year: number, month: number) => void;
}

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

const toLocalDateStr = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;

const FC_OVERRIDE_STYLES = {
  '.mini-calendar-fc': {
    fontFamily: 'inherit',
    overflow: 'hidden',
    '& td, & th': { border: 'none !important' },
    '& .fc-scrollgrid': { border: 'none !important', overflow: 'hidden !important' },
    '& .fc-scrollgrid-sync-table': {
      borderCollapse: 'collapse',
      tableLayout: 'fixed !important',
      width: '100% !important',
      minWidth: '0 !important',
    },
    '& .fc-view-harness': { overflow: 'hidden !important' },
    '& .fc-scroller': { overflow: 'hidden !important' },
    '& .fc-daygrid-day-frame': {
      padding: '0 !important',
      minHeight: '0 !important',
    },
    '& .fc-daygrid-day': {
      padding: '0 !important',
      verticalAlign: 'middle !important',
    },
    '& .fc-daygrid-day-top': {
      float: 'none !important',
      display: 'flex !important',
      justifyContent: 'center !important',
      padding: '0 !important',
    },
    '& .fc-daygrid-day-number': {
      padding: '0 !important',
      width: '100% !important',
      textDecoration: 'none !important',
      cursor: 'pointer !important',
      color: 'inherit !important',
    },
    '& .fc-daygrid-day-events': { display: 'none !important' },
    '& .fc-daygrid-day-bg': { display: 'none !important' },
    '& .fc-day-today': { backgroundColor: 'transparent !important' },
    '& .fc-col-header-cell': {
      border: 'none !important',
      padding: '2px 0 6px !important',
    },
    '& .fc-col-header-cell-cushion': { padding: '0 !important' },
    '& .fc-day-other': { backgroundColor: 'transparent !important' },
  },
};

export const MiniCalendar: React.FC<MiniCalendarProps> = ({
  year,
  month,
  calendarDots,
  selectedDate,
  onDateClick,
  onMonthChange,
}) => {
  const calendarRef = useRef<FullCalendar>(null);
  const isInternalNavRef = useRef(false);
  const dotSet = useMemo(
    () => new Set(calendarDots.map((d) => d.date)),
    [calendarDots],
  );

  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    const curr = api.getDate();
    if (curr.getFullYear() !== year || curr.getMonth() + 1 !== month) {
      api.gotoDate(`${year}-${String(month).padStart(2, '0')}-01`);
    }
  }, [year, month]);

  const handlePrev = () => {
    isInternalNavRef.current = true;
    calendarRef.current?.getApi().prev();
  };

  const handleNext = () => {
    isInternalNavRef.current = true;
    calendarRef.current?.getApi().next();
  };

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      if (!isInternalNavRef.current) return;
      isInternalNavRef.current = false;
      const start = arg.view.currentStart;
      onMonthChange(start.getFullYear(), start.getMonth() + 1);
    },
    [onMonthChange],
  );

  const handleDateClick = useCallback(
    (arg: DateClickArg) => {
      arg.jsEvent.preventDefault();
      onDateClick(arg.dateStr);
    },
    [onDateClick],
  );

  const renderDayHeader = useCallback((arg: { dow: number }) => {
    const isSun = arg.dow === 0;
    const isSat = arg.dow === 6;
    return (
      <Typography
        component="span"
        sx={{
          color: isSun ? '#EF5350' : isSat ? '#1976D2' : '#616161',
          fontWeight: 600,
          fontSize: '0.85rem',
        }}
      >
        {WEEKDAY_LABELS[arg.dow]}
      </Typography>
    );
  }, []);

  const renderDayCell = useCallback(
    (arg: DayCellContentArg) => {
      if (arg.isOther) {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 0.75,
            }}
          >
            <Box
              sx={{
                width: 'min(42px, 80%)',
                aspectRatio: '1 / 1',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#BDBDBD',
                fontSize: 'clamp(0.75rem, 1.5vw, 1rem)',
                flexShrink: 0,
              }}
            >
              {arg.date.getDate()}
            </Box>
            <Box sx={{ width: 5, height: 5, mt: 0.25, flexShrink: 0 }} />
          </Box>
        );
      }

      const dateStr = toLocalDateStr(arg.date);
      const hasDot = dotSet.has(dateStr);
      const isSelected = dateStr === selectedDate;
      const dow = arg.date.getDay();
      const isSun = dow === 0;
      const isSat = dow === 6;

      const textColor = isSelected
        ? '#fff'
        : isSun
          ? '#EF5350'
          : isSat
            ? '#1976D2'
            : '#212121';

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 0.75,
            borderRadius: isSelected ? '8px' : 0,
            backgroundColor: isSelected ? '#1976D2' : 'transparent',
            transition: 'background-color 0.15s',
          }}
        >
          <Box
            sx={{
              width: 'min(42px, 80%)',
              aspectRatio: '1 / 1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              backgroundColor: isSelected ? 'transparent' : arg.isToday ? '#E3F2FD' : 'transparent',
              color: textColor,
              fontWeight: arg.isToday || isSelected ? 700 : 400,
              fontSize: 'clamp(0.75rem, 1.5vw, 1rem)',
              flexShrink: 0,
            }}
          >
            {arg.date.getDate()}
          </Box>
          <Box
            sx={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              mt: 0.25,
              flexShrink: 0,
              backgroundColor: hasDot
                ? isSelected
                  ? '#fff'
                  : '#1976D2'
                : 'transparent',
            }}
          />
        </Box>
      );
    },
    [dotSet, selectedDate],
  );

  return (
    <Box sx={{ userSelect: 'none', width: '100%', overflow: 'hidden', minWidth: 0 }}>
      <GlobalStyles styles={FC_OVERRIDE_STYLES} />

      {/* ヘッダー */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
          {year}年{month}月
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton
            size="small"
            onClick={handlePrev}
            sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 0.5 }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={handleNext}
            sx={{ border: '1px solid #E0E0E0', borderRadius: 1, p: 0.5 }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* FullCalendar 本体（月ビューのみ） */}
      <Box className="mini-calendar-fc">
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          initialDate={`${year}-${String(month).padStart(2, '0')}-01`}
          headerToolbar={false}
          locale="ja"
          firstDay={0}
          fixedWeekCount={false}
          showNonCurrentDates={true}
          dayHeaderContent={renderDayHeader}
          dayCellContent={renderDayCell}
          datesSet={handleDatesSet}
          dateClick={handleDateClick}
          height="auto"
          contentHeight="auto"
          events={[]}
        />
      </Box>
    </Box>
  );
};
